import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAudio } from '../contexts/AudioContext';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Button from '../components/Button';
import MoodboardSelector from '../components/MoodboardSelector';
import TipTapEditor from '../components/TipTapEditor';
import AudioUploader from '../components/AudioUploader';
import ImageUploader from '../components/ImageUploader';
import config from '../config';

const SongDetail = () => {
  const { songId } = useParams();
  const navigate = useNavigate();
  const [song, setSong] = useState(null);
  const [album, setAlbum] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('lyrics');
  const [editMode, setEditMode] = useState(true);
  const [albums, setAlbums] = useState([]);
  const [showAlbumSelector, setShowAlbumSelector] = useState(false);
  const [showThumbnailUploader, setShowThumbnailUploader] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'error'
  const { playTrack, currentTrack, isPlaying } = useAudio();
  const saveTimeoutRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    lyrics: '',
    notes: '',
    thumbnailUrl: '',
    thumbnailKey: ''
  });
  
  // Fetch all albums for the album selector
  useEffect(() => {
    const fetchAlbums = async () => {
      try {
        const response = await axios.get(`${config.API_URL}/albums`);
        setAlbums(response.data || []);
      } catch (error) {
        console.error('Error fetching albums:', error);
      }
    };
    
    fetchAlbums();
  }, []);
  
  // Debounced save function for autosave
  const debouncedSave = useCallback(() => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set saving status
    setSaveStatus('saving');
    
    // Set a new timeout
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await axios.put(`${config.API_URL}/songs/${songId}`, {
          ...formData,
          username: config.USERNAME
        });
        
        // Update the song state with the new data
        setSong(prev => ({
          ...prev,
          ...formData
        }));
        
        // Set saved status
        setSaveStatus('saved');
      } catch (err) {
        console.error('Error auto-saving song:', err);
        setSaveStatus('error');
      }
    }, 1500); // 1.5 second delay for autosave
  }, [formData, songId]);

  // Fetch pre-signed URL for audio files if they exist
  const fetchPresignedUrls = async (audioFiles) => {
    if (!audioFiles || audioFiles.length === 0) return audioFiles;
    
    console.log('Fetching pre-signed URLs for audio files:', audioFiles.map(file => ({
      id: file.id,
      name: file.name,
      hasFileKey: !!file.fileKey,
      fileKey: file.fileKey
    })));
    
    const updatedAudioFiles = await Promise.all(audioFiles.map(async (file) => {
      if (file.fileKey) {
        try {
          console.log('Fetching pre-signed URL for file with key:', file.fileKey);
          const response = await axios.get(`${config.API_URL}/get-presigned-url?key=${encodeURIComponent(file.fileKey)}`);
          if (response.data && response.data.presignedUrl) {
            console.log('Successfully fetched pre-signed URL for file:', file.name);
            return { ...file, url: response.data.presignedUrl };
          } else {
            console.error('No presigned URL in response for file:', file.name);
          }
        } catch (error) {
          console.error(`Error fetching pre-signed URL for audio file ${file.name}:`, error);
        }
      } else {
        console.warn('Audio file missing fileKey:', file.name, file.id);
      }
      return file;
    }));
    
    console.log('Updated audio files with fresh pre-signed URLs:', updatedAudioFiles.length);
    return updatedAudioFiles;
  };
  
  useEffect(() => {
    const fetchSongDetails = async () => {
      setIsLoading(true);
      try {
        // Fetch song details
        const songResponse = await axios.get(`${config.API_URL}/songs/${songId}`);
        const songData = songResponse.data;
        
        // DEVELOPMENT WORKAROUND: Check localStorage for stored audio files
        // This is a temporary workaround for CORS issues
        let localStorageAudioFiles = null;
        try {
          const storedAudioFiles = localStorage.getItem(`song_${songId}_audioFiles`);
          if (storedAudioFiles) {
            localStorageAudioFiles = JSON.parse(storedAudioFiles);
            console.log('Found stored audio files in localStorage:', localStorageAudioFiles.length);
          }
        } catch (e) {
          console.error('Error accessing localStorage:', e);
        }
        
        // Check for thumbnail in localStorage (for CORS workaround)
        try {
          const storedThumbnail = localStorage.getItem(`song_${songId}_thumbnail`);
          if (storedThumbnail) {
            const thumbnailData = JSON.parse(storedThumbnail);
            console.log('Found stored thumbnail in localStorage:', thumbnailData);
            
            // Use the localStorage thumbnail if it's newer than what's in the API response
            if (thumbnailData.thumbnailUrl && thumbnailData.thumbnailKey) {
              if (!songData.thumbnailKey || !songData.updatedAt || 
                  (thumbnailData.timestamp && new Date(thumbnailData.timestamp) > new Date(songData.updatedAt))) {
                console.log('Using more recent thumbnail from localStorage');
                songData.thumbnailUrl = thumbnailData.thumbnailUrl;
                songData.thumbnailKey = thumbnailData.thumbnailKey;
              }
            }
          }
        } catch (e) {
          console.error('Error accessing localStorage for thumbnail:', e);
        }
        
        // Use audio files from the API response if available, otherwise fall back to localStorage
        if (songData.audioFiles && songData.audioFiles.length > 0) {
          console.log('Song has audio files from API, fetching fresh pre-signed URLs:', songData.audioFiles.length);
          songData.audioFiles = await fetchPresignedUrls(songData.audioFiles);
        } else if (localStorageAudioFiles && localStorageAudioFiles.length > 0) {
          console.log('Using audio files from localStorage as fallback');
          songData.audioFiles = await fetchPresignedUrls(localStorageAudioFiles);
        } else {
          console.log('Song has no audio files (neither from API nor localStorage)');
          songData.audioFiles = [];
        }
        
        // Get pre-signed URL for thumbnail if it exists
        if (songData.thumbnailKey) {
          try {
            const response = await axios.get(`${config.API_URL}/get-presigned-url?key=${encodeURIComponent(songData.thumbnailKey)}`);
            if (response.data && response.data.presignedUrl) {
              songData.thumbnailUrl = response.data.presignedUrl;
            }
          } catch (error) {
            console.error('Error fetching pre-signed URL for thumbnail:', error);
          }
        }
        
        setSong(songData);
        setFormData({
          title: songData.title || '',
          lyrics: songData.lyrics || '',
          notes: songData.notes || ''
        });
        
        // If song has an albumId, fetch the album details
        if (songResponse.data.albumId) {
          try {
            const albumResponse = await axios.get(`${config.API_URL}/albums/${songResponse.data.albumId}`);
            setAlbum(albumResponse.data);
          } catch (albumErr) {
            // Album might have been deleted, handle gracefully
            console.log('Album not found or was deleted:', albumErr);
            // Don't set error state for the whole page just because album is missing
          }
        }
      } catch (err) {
        console.error('Error fetching song details:', err);
        setError('Failed to load song details. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSongDetails();
  }, [songId]);
  
  // No need for audio cleanup as it's handled by the AudioContext
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-24 h-24 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-white animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p className="mt-4 text-lg font-medium text-gray-600">Loading song details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md inline-block">
          <p>{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!song) {
    return (
      <div className="text-center py-10">
        <p className="text-lg text-gray-600">Song not found</p>
        <button 
          onClick={() => navigate('/')}
          className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    debouncedSave();
  };

  // Manual save function
  const handleSave = async () => {
    try {
      setSaveStatus('saving');
      await axios.put(`${config.API_URL}/songs/${songId}`, {
        ...formData,
        username: config.USERNAME
      });
      setSong(prev => ({
        ...prev,
        ...formData
      }));
      setSaveStatus('saved');
    } catch (err) {
      console.error('Error updating song:', err);
      setSaveStatus('error');
      alert('Failed to update song. Please try again.');
    }
  };
  
  return (
    <div className="bg-gradient-to-br from-black via-purple-900 to-indigo-900 min-h-screen -mt-8 pt-8 pb-16">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/" className="text-gray-300 hover:text-white flex items-center transition-colors duration-200">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to Library
          </Link>
        </div>
        
        <div className="bg-gradient-to-r from-gray-900 to-black backdrop-filter backdrop-blur-lg bg-opacity-90 rounded-2xl shadow-2xl overflow-hidden mb-8 border border-purple-900 transform hover:scale-[1.01] transition-all duration-300">
          <div className="md:flex">
            <div className="md:w-1/3 lg:w-1/4">
              <div className="relative aspect-square group">
                {/* Hidden file input for direct thumbnail upload */}
                <input 
                  type="file" 
                  accept="image/*" 
                  id="thumbnail-upload"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    
                    try {
                      // Show loading state
                      const thumbnailEl = document.getElementById('song-thumbnail');
                      if (thumbnailEl) {
                        thumbnailEl.classList.add('opacity-50');
                      }
                      
                      // Request a pre-signed upload URL
                      const fileType = file.type;
                      const response = await axios.get(`${config.API_URL}/upload-url`, {
                        params: {
                          fileType,
                          songId,
                          type: 'thumbnail'
                        }
                      });

                      const { uploadUrl, fileUrl, fileKey } = response.data;

                      // Upload the file directly to S3
                      await axios.put(uploadUrl, file, {
                        headers: {
                          'Content-Type': fileType
                        }
                      });

                      // Update song with new thumbnail image
                      const updatedSong = { 
                        ...song, 
                        thumbnailUrl: fileUrl,
                        thumbnailKey: fileKey
                      };
                      
                      // Update song in database
                      await axios.put(`${config.API_URL}/songs/${songId}`, updatedSong);
                      setSong(updatedSong);
                      
                      // Store thumbnail in localStorage as a CORS workaround
                      try {
                        localStorage.setItem(`song_${songId}_thumbnail`, JSON.stringify({
                          thumbnailUrl: fileUrl,
                          thumbnailKey: fileKey,
                          timestamp: new Date().toISOString()
                        }));
                      } catch (e) {
                        console.error('Error saving thumbnail to localStorage:', e);
                      }
                      
                      // Reset the file input
                      e.target.value = null;
                      
                    } catch (err) {
                      console.error('Error uploading thumbnail:', err);
                      alert('Failed to upload thumbnail. Please try again.');
                    } finally {
                      // Remove loading state
                      const thumbnailEl = document.getElementById('song-thumbnail');
                      if (thumbnailEl) {
                        thumbnailEl.classList.remove('opacity-50');
                      }
                    }
                  }}
                />
                
                {song.thumbnailUrl || song.thumbnailKey ? (
                  <div className="relative">
                    <img 
                      id="song-thumbnail"
                      src={song.thumbnailUrl} 
                      alt={song.title} 
                      className="w-full h-full object-cover transition-all duration-300 group-hover:brightness-75"
                      onError={(e) => {
                        console.error('Thumbnail failed to load:', song.thumbnailUrl);
                        e.target.onerror = null; // Prevent infinite error loop
                        // Fallback to music icon
                        e.target.style.display = 'none';
                        e.target.parentNode.innerHTML = `
                          <div class="w-full h-full bg-gradient-to-br from-purple-600 via-fuchsia-500 to-indigo-800 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-24 w-24 text-white opacity-75" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                            </svg>
                          </div>
                        `;
                      }}
                    />
                    
                    {/* Upload overlay - appears on hover */}
                    <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <button
                        onClick={() => document.getElementById('thumbnail-upload').click()}
                        className="absolute top-4 right-4 p-2 bg-indigo-600 rounded-full text-white hover:bg-indigo-500 transform hover:scale-110 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 focus:ring-offset-gray-900 shadow-lg"
                        title="Upload new thumbnail"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="w-full h-full bg-gradient-to-br from-purple-600 via-fuchsia-500 to-indigo-800 flex items-center justify-center cursor-pointer group-hover:brightness-75 transition-all duration-300"
                    onClick={() => document.getElementById('thumbnail-upload').click()}
                  >
                    <div className="flex flex-col items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 text-white opacity-75" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                      </svg>
                      <div className="mt-2 text-white text-opacity-90 text-sm font-medium">Click to add thumbnail</div>
                    </div>
                  </div>
                )}
                
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                  <div className="flex items-center space-x-2">
                    <button 
                      className="p-3 rounded-full bg-green-500 text-white hover:bg-green-400 transform hover:scale-105 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2 focus:ring-offset-gray-900 shadow-lg"
                      onClick={() => {
                        // Find the default audio file or the first one if no default is set
                        const audioFiles = song.audioFiles || [];
                        const defaultAudio = audioFiles.find(file => file.isDefault) || audioFiles[0];
                        
                        if (defaultAudio && defaultAudio.url) {
                          // Prepare track object for the global audio player
                          const track = {
                            id: defaultAudio.id || `song-${song.id}-audio-${Date.now()}`,
                            title: song.title,
                            albumTitle: album ? album.title : 'No Album',
                            audioUrl: defaultAudio.url,
                            fileKey: defaultAudio.fileKey,
                            thumbnailUrl: song.thumbnailUrl
                          };
                          
                          // Play the track using the global audio player
                          playTrack(track);
                        } else {
                          alert('No audio file available. Please upload an audio file first.');
                          setActiveTab('audio');
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
                        {isPlaying && currentTrack && currentTrack.title === song.title ? (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                        ) : (
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                        )}
                      </svg>
                    </button>
                    <div className="text-white font-medium text-shadow">{isPlaying ? 'Pause' : 'Play Demo'}</div>
                    

                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 md:w-2/3 lg:w-3/4 text-white">
              {editMode ? (
                <div>
                  <div className="mb-4">
                    <label className="block text-gray-300 text-sm font-bold mb-2" htmlFor="title">
                      Title
                    </label>
                    <input
                      id="title"
                      name="title"
                      type="text"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="shadow appearance-none border border-gray-600 bg-gray-700 rounded w-full py-2 px-3 text-white leading-tight focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div className="flex justify-end mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="mr-2"
                      onClick={() => setEditMode(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleSave}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-start">
                  <h1 className="text-4xl font-bold text-white mb-2">{song.title}</h1>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setEditMode(true)}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="danger" 
                      size="sm"
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete this song?')) {
                          axios.delete(`${config.API_URL}/songs/${songId}`)
                            .then(() => {
                              navigate('/');
                            })
                            .catch(error => {
                              console.error('Error deleting song:', error);
                              alert('Failed to delete song. Please try again.');
                            });
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Album information section */}
              {!showAlbumSelector ? (
                <>
                  {song.albumId && album && (
                    <div className="mb-4 flex items-center">
                      <span className="text-gray-400">From album: </span>
                      <Link 
                        to={`/albums/${album.id}`}
                        className="text-indigo-400 hover:text-indigo-300 transition-colors mx-2"
                      >
                        {album.title}
                      </Link>
                      <button
                        onClick={() => setShowAlbumSelector(true)}
                        className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                      >
                        Change Album
                      </button>
                    </div>
                  )}
                  
                  {song.albumId && !album && (
                    <div className="mb-4 flex items-center">
                      <span className="text-gray-400">Album: </span>
                      <span className="text-amber-400 mx-2">
                        Album not found (it may have been deleted)
                      </span>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            // Update song to remove the albumId
                            const updatedSong = { ...song, albumId: null };
                            axios.put(`${config.API_URL}/songs/${songId}`, updatedSong)
                              .then(() => {
                                setSong(updatedSong);
                              })
                              .catch(error => {
                                console.error('Error updating song:', error);
                              });
                          }}
                          className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                        >
                          Clear reference
                        </button>
                        <button
                          onClick={() => setShowAlbumSelector(true)}
                          className="text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
                        >
                          Assign to Album
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {!song.albumId && (
                    <div className="mb-4">
                      <button
                        onClick={() => setShowAlbumSelector(true)}
                        className="text-sm text-indigo-400 hover:text-indigo-300 transition-colors flex items-center"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add to Album
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="mb-4">
                  <h3 className="text-gray-400 mb-2">Select Album:</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto bg-gray-800 p-2 rounded">
                    {albums.length > 0 ? (
                      albums.map(album => (
                        <button
                          key={album.id}
                          onClick={() => {
                            // Update song with new albumId
                            const updatedSong = { ...song, albumId: album.id };
                            axios.put(`${config.API_URL}/songs/${songId}`, updatedSong)
                              .then(() => {
                                setSong(updatedSong);
                                setAlbum(album);
                                setShowAlbumSelector(false);
                              })
                              .catch(error => {
                                console.error('Error updating song:', error);
                              });
                          }}
                          className="text-left p-2 hover:bg-gray-700 rounded flex items-center"
                        >
                          <div className="w-8 h-8 bg-gray-600 rounded flex items-center justify-center mr-2">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                            </svg>
                          </div>
                          <div className="text-white">{album.title}</div>
                        </button>
                      ))
                    ) : (
                      <div className="text-gray-400 p-2">No albums available</div>
                    )}
                  </div>
                  <div className="mt-2 flex justify-end">
                    <button
                      onClick={() => setShowAlbumSelector(false)}
                      className="text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              
              <div className="flex flex-wrap items-center text-sm text-gray-300 mb-6">
                {song.duration && (
                  <div className="mr-6 mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{song.duration}</span>
                  </div>
                )}
                
                {song.audioFiles && song.audioFiles.length > 0 && (
                  <div className="mr-6 mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                    </svg>
                    <span>{song.audioFiles.length} audio version{song.audioFiles.length !== 1 ? 's' : ''}</span>
                  </div>
                )}
                
                {song.createdAt && (
                  <div className="mr-6 mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Created: {new Date(song.createdAt).toLocaleDateString()}</span>
                  </div>
                )}
                
                {song.updatedAt && song.updatedAt !== song.createdAt && (
                  <div className="mr-6 mb-2 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Updated: {new Date(song.updatedAt).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
              
              {/* No duration input field needed as it will be auto-calculated */}
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <div className="border-b border-gray-700">
            <nav className="-mb-px flex space-x-8">
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-all duration-200 ${activeTab === 'lyrics' ? 'border-green-500 text-green-400 font-semibold' : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'}`}
                onClick={() => setActiveTab('lyrics')}
              >
                Lyrics
              </button>
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-all duration-200 ${activeTab === 'notes' ? 'border-green-500 text-green-400 font-semibold' : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'}`}
                onClick={() => setActiveTab('notes')}
              >
                Notes
              </button>
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-all duration-200 ${activeTab === 'audio' ? 'border-green-500 text-green-400 font-semibold' : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'}`}
                onClick={() => setActiveTab('audio')}
              >
                Audio Files
              </button>
              <button
                className={`py-2 px-4 border-b-2 font-medium text-sm transition-all duration-200 ${activeTab === 'image' ? 'border-green-500 text-green-400 font-semibold' : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-600'}`}
                onClick={() => setActiveTab('image')}
              >
                Image
              </button>
            </nav>
          </div>
        </div>
        
        <div className="bg-gradient-to-r from-gray-900 to-black backdrop-filter backdrop-blur-lg bg-opacity-80 rounded-xl shadow-xl p-6 border border-purple-900 text-white transform hover:scale-[1.01] transition-all duration-300">
          {activeTab === 'lyrics' && (
            <div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-gray-300 text-sm font-bold" htmlFor="lyrics">
                    Lyrics
                  </label>
                  <div className="flex items-center">
                    {saveStatus === 'saving' && (
                      <span className="text-yellow-400 text-xs mr-2 flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    )}
                    {saveStatus === 'saved' && (
                      <span className="text-green-400 text-xs mr-2 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Saved
                      </span>
                    )}
                    {saveStatus === 'error' && (
                      <span className="text-red-400 text-xs mr-2 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        Error Saving
                      </span>
                    )}
                    <Button 
                      variant="primary" 
                      size="sm"
                      className="mr-2 flex items-center"
                      onClick={async () => {
                        try {
                          setSaveStatus('saving');
                          const response = await axios.post(`${config.API_URL}/ai/generate`, {
                            type: 'song_lyrics',
                            context: {
                              songTitle: formData.title,
                              albumTitle: album ? album.title : null,
                              albumDescription: album ? album.description : null
                            }
                          });
                          
                          if (response.data && response.data.content) {
                            // Update the song with the AI-generated lyrics
                            const newLyrics = formData.lyrics ? formData.lyrics + '\n\n' + response.data.content : response.data.content;
                            setFormData(prev => ({
                              ...prev,
                              lyrics: newLyrics
                            }));
                            debouncedSave();
                          }
                          setSaveStatus('saved');
                        } catch (error) {
                          console.error('Error generating AI lyrics:', error);
                          setSaveStatus('error');
                          alert('Failed to generate AI lyrics. Please try again.');
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      AI Lyrics
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleSave}
                    >
                      Save Lyrics
                    </Button>
                  </div>
                </div>
                <TipTapEditor
                  content={formData.lyrics || ''}
                  onChange={(content) => {
                    setFormData(prev => ({...prev, lyrics: content}));
                    debouncedSave();
                  }}
                  placeholder="Write your lyrics here..."
                />
              </div>
            </div>
          )}
          
          {activeTab === 'notes' && (
            <div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-gray-300 text-sm font-bold" htmlFor="notes">
                    Notes
                  </label>
                  <div className="flex items-center">
                    {saveStatus === 'saving' && (
                      <span className="text-yellow-400 text-xs mr-2 flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    )}
                    {saveStatus === 'saved' && (
                      <span className="text-green-400 text-xs mr-2 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Saved
                      </span>
                    )}
                    {saveStatus === 'error' && (
                      <span className="text-red-400 text-xs mr-2 flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        Error Saving
                      </span>
                    )}
                    <Button 
                      variant="primary" 
                      size="sm"
                      className="mr-2 flex items-center"
                      onClick={async () => {
                        try {
                          setSaveStatus('saving');
                          const response = await axios.post(`${config.API_URL}/ai/generate`, {
                            type: 'song_notes',
                            context: {
                              songTitle: formData.title,
                              songLyrics: formData.lyrics,
                              albumTitle: album ? album.title : null,
                              albumDescription: album ? album.description : null
                            }
                          });
                          
                          if (response.data && response.data.content) {
                            // Update the song with the AI-generated notes
                            const newNotes = formData.notes ? formData.notes + '\n\n' + response.data.content : response.data.content;
                            setFormData(prev => ({
                              ...prev,
                              notes: newNotes
                            }));
                            debouncedSave();
                          }
                          setSaveStatus('saved');
                        } catch (error) {
                          console.error('Error generating AI notes:', error);
                          setSaveStatus('error');
                          alert('Failed to generate AI notes. Please try again.');
                        }
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      AI Notes
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleSave}
                    >
                      Save Notes
                    </Button>
                  </div>
                </div>
                <TipTapEditor
                  content={formData.notes || ''}
                  onChange={(content) => {
                    setFormData(prev => ({...prev, notes: content}));
                    debouncedSave();
                  }}
                  placeholder="Add notes, ideas, or thoughts about this song..."
                />
              </div>
            </div>
          )}
        </div>
        
        {activeTab === 'audio' && (
          <div className="bg-gradient-to-r from-gray-900 to-black backdrop-filter backdrop-blur-lg bg-opacity-80 rounded-xl shadow-xl p-6 border border-purple-900 text-white transform hover:scale-[1.01] transition-all duration-300">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071a1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243a1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828a1 1 0 010-1.415z" clipRule="evenodd" />
                </svg>
                Audio Files
              </h2>
            </div>
            
            <AudioUploader
              songId={songId}
              existingAudioFiles={song.audioFiles || []}
              onAudioAdded={(audioFiles, duration) => {
                // Update song with new audio files and duration (if provided)
                const updatedSong = { ...song, audioFiles };
                
                // If there's a default version, use its duration
                const defaultVersion = audioFiles.find(file => file.isDefault);
                if (defaultVersion && defaultVersion.durationSeconds) {
                  updatedSong.duration = defaultVersion.duration;
                } else if (duration && (!song.duration || song.audioFiles?.length === 0)) {
                  updatedSong.duration = duration;
                }
                
                // Make sure we're saving the fileKeys for persistence
                if (audioFiles && audioFiles.length > 0) {
                  console.log('Saving audio files with fileKeys:', 
                    audioFiles.map(file => ({
                      id: file.id,
                      name: file.name,
                      hasFileKey: !!file.fileKey,
                      fileKey: file.fileKey
                    }))
                  );
                  
                  // Validate that all files have fileKeys and ensure they're included
                  const sanitizedAudioFiles = audioFiles.map(file => {
                    // Create a clean copy of the file object with only necessary properties
                    const sanitizedFile = {
                      id: file.id,
                      name: file.name,
                      description: file.description || '',
                      fileKey: file.fileKey, // This is critical for persistence!
                      fileType: file.fileType,
                      fileSize: file.fileSize,
                      duration: file.duration,
                      durationSeconds: file.durationSeconds,
                      uploadedAt: file.uploadedAt,
                      isDefault: file.isDefault
                    };
                    
                    // Include url for immediate use but it's not critical for persistence
                    if (file.url) {
                      sanitizedFile.url = file.url;
                    }
                    
                    if (!file.fileKey) {
                      console.error('Audio file missing fileKey, it will not persist after refresh:', file.name, file.id);
                    }
                    
                    return sanitizedFile;
                  });
                  
                  // Use the sanitized audio files to ensure consistent data structure
                  updatedSong.audioFiles = sanitizedAudioFiles;
                  
                  // Final verification of fileKeys before saving
                  const missingFileKeys = sanitizedAudioFiles.filter(file => !file.fileKey);
                  if (missingFileKeys.length > 0) {
                    console.error('CRITICAL ERROR: Audio files missing fileKeys before database save:', 
                      missingFileKeys.map(f => ({ id: f.id, name: f.name })));
                  }
                }
                
                // Update song in database
                console.log('Updating song with audio files in database:', JSON.stringify(updatedSong.audioFiles));
                console.log('Full API URL being called:', `${config.API_URL}/songs/${songId}`);
                console.log('Full song data being sent:', JSON.stringify(updatedSong));
                
                // DEVELOPMENT WORKAROUND: Store audio files in localStorage as a fallback
                try {
                  localStorage.setItem(`song_${songId}_audioFiles`, JSON.stringify(updatedSong.audioFiles));
                  console.log('Stored audio files in localStorage as fallback for CORS issues');
                } catch (e) {
                  console.error('Error storing audio files in localStorage:', e);
                }
                
                // Add a timestamp to track when this request was made
                const requestTime = new Date().toISOString();
                console.log(`API request started at ${requestTime}`);
                
                // Create a new URL object to get the base URL for CORS Origin
                const apiUrl = new URL(config.API_URL);
                const origin = window.location.origin; // Get current origin
                
                axios.put(`${config.API_URL}/songs/${songId}`, updatedSong, {
                  // Add necessary headers for CORS
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Debug-Timestamp': requestTime,
                    'Accept': 'application/json',
                    'Origin': origin
                  },
                  // Enable withCredentials for CORS with cookies if needed
                  withCredentials: false
                })
                  .then(response => {
                    console.log(`API request completed successfully at ${new Date().toISOString()}`);
                    console.log('API response:', response.data);
                    console.log('Successfully updated song with audio files');
                    setSong(updatedSong);
                    
                    // Verify the data was saved by immediately fetching it back
                    console.log('Verifying data persistence by fetching song again...');
                    setTimeout(() => {
                      axios.get(`${config.API_URL}/songs/${songId}`)
                        .then(verifyResponse => {
                          console.log('Verification fetch successful:', verifyResponse.data);
                          console.log('Saved audio files:', JSON.stringify(verifyResponse.data.audioFiles));
                          if (verifyResponse.data.audioFiles && verifyResponse.data.audioFiles.length > 0) {
                            console.log('Audio files were properly saved to the database!');
                          } else {
                            console.error('Audio files were NOT saved to the database!');
                          }
                        })
                        .catch(verifyError => {
                          console.error('Error verifying data persistence:', verifyError);
                        });
                    }, 1000); // Wait 1 second before verifying
                  })
                  .catch(error => {
                    console.error(`API request failed at ${new Date().toISOString()}`);
                    console.error('Error updating song with audio files:', error);
                    if (error.response) {
                      console.error('Error response data:', error.response.data);
                      console.error('Error response status:', error.response.status);
                      console.error('Error response headers:', error.response.headers);
                    } else if (error.request) {
                      console.error('Error request:', error.request);
                    } else {
                      console.error('Error message:', error.message);
                    }
                  });
              }}
            />
          </div>
        )}
        
        {activeTab === 'debug' && (
          <div className="bg-gradient-to-r from-gray-900 to-black backdrop-filter backdrop-blur-lg rounded-xl shadow-xl p-6 border border-purple-900 text-white transform hover:scale-[1.01] transition-all duration-300">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white flex items-center">
                Debug Information
              </h2>
            </div>
            <div className="bg-gray-800 p-4 rounded-md overflow-auto max-h-96">
              <h3 className="text-lg font-medium mb-2">Audio Files in State:</h3>
              <pre className="text-xs text-green-300 whitespace-pre-wrap">
                {JSON.stringify(song.audioFiles || [], null, 2)}
              </pre>
              
              <h3 className="text-lg font-medium mt-4 mb-2">Test API Calls:</h3>
              <div className="flex space-x-2">
                <button
                  className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm"
                  onClick={() => {
                    // Test GET request
                    console.log('Testing GET request...');
                    axios.get(`${config.API_URL}/songs/${songId}`)
                      .then(response => {
                        console.log('GET response:', response.data);
                        alert(`GET successful! Audio files: ${JSON.stringify(response.data.audioFiles?.length || 0)}`);
                      })
                      .catch(error => {
                        console.error('GET error:', error);
                        alert(`GET failed: ${error.message}`);
                      });
                  }}
                >
                  Test GET
                </button>
                
                <button
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md text-sm"
                  onClick={() => {
                    // Test PUT request with current song data
                    console.log('Testing PUT request with current song data...');
                    const testSong = {...song, testField: Date.now()};
                    axios.put(`${config.API_URL}/songs/${songId}`, testSong, {
                      headers: {
                        'Content-Type': 'application/json'
                      }
                    })
                      .then(response => {
                        console.log('PUT response:', response.data);
                        alert(`PUT successful! Response: ${JSON.stringify(response.data)}`);
                      })
                      .catch(error => {
                        console.error('PUT error:', error);
                        alert(`PUT failed: ${error.message}`);
                      });
                  }}
                >
                  Test PUT
                </button>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'image' && (
          <div className="bg-gradient-to-r from-gray-900 to-black backdrop-filter backdrop-blur-lg bg-opacity-80 rounded-xl shadow-xl p-6 border border-purple-900 text-white transform hover:scale-[1.01] transition-all duration-300">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
                Song Thumbnail
              </h2>
            </div>
            
            <ImageUploader
              songId={songId}
              existingImageUrl={song.thumbnailUrl}
              existingImageKey={song.thumbnailKey}
              onImageAdded={(imageUrl, imageKey) => {
                // Update song with new thumbnail image
                const updatedSong = { 
                  ...song, 
                  thumbnailUrl: imageUrl,
                  thumbnailKey: imageKey
                };
                
                // Update song in database
                axios.put(`${config.API_URL}/songs/${songId}`, updatedSong)
                  .then(() => {
                    setSong(updatedSong);
                    
                    // Store thumbnail in localStorage as a CORS workaround
                    try {
                      localStorage.setItem(`song_${songId}_thumbnail`, JSON.stringify({
                        thumbnailUrl: imageUrl,
                        thumbnailKey: imageKey,
                        timestamp: new Date().toISOString()
                      }));
                      console.log('Saved thumbnail to localStorage as CORS workaround');
                    } catch (e) {
                      console.error('Error saving thumbnail to localStorage:', e);
                    }
                  })
                  .catch(error => {
                    console.error('Error updating song with thumbnail:', error);
                  });
              }}
            />
          </div>
        )}
        
        {/* Thumbnail uploader modal */}
        {showThumbnailUploader && (
          <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-75">
            <div className="max-w-md w-full">
              <ImageUploader
                songId={songId}
                existingImageUrl={song.thumbnailUrl}
                existingImageKey={song.thumbnailKey}
                onImageAdded={(imageUrl, imageKey) => {
                  // Update song with new thumbnail image
                  const updatedSong = { 
                    ...song, 
                    thumbnailUrl: imageUrl,
                    thumbnailKey: imageKey
                  };
                  
                  // Update song in database
                  axios.put(`${config.API_URL}/songs/${songId}`, updatedSong)
                    .then(() => {
                      setSong(updatedSong);
                      
                      // Store thumbnail in localStorage as a CORS workaround
                      try {
                        localStorage.setItem(`song_${songId}_thumbnail`, JSON.stringify({
                          thumbnailUrl: imageUrl,
                          thumbnailKey: imageKey,
                          timestamp: new Date().toISOString()
                        }));
                        console.log('Saved thumbnail to localStorage as CORS workaround');
                      } catch (e) {
                        console.error('Error saving thumbnail to localStorage:', e);
                      }
                      
                      // Close the uploader modal
                      setShowThumbnailUploader(false);
                    })
                    .catch(error => {
                      console.error('Error updating song with thumbnail:', error);
                    });
                }}
                onClose={() => setShowThumbnailUploader(false)}
              />
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default SongDetail;
